import React, { ElementRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { css } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { Entity } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/entity.pb';
import { UiSource } from '@weave/schema-gen-ts/dist/shared/wsearch/v1/ui_source.pb';
import useMeasure from 'react-use-measure';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { handleTriggerSearch, useSearchHistoryShallowStore } from '@frontend/search-history';
import { useShellTheme } from '@frontend/shell-theme';
import { shell } from '@frontend/shell-utils';
import {
  PopoverDialog,
  usePopoverDialog,
  useControlledField,
  useDebouncedFn,
  useEventListener,
  Button,
} from '@frontend/design-system';
import { GlobalSearchTrackingIds } from '../constants';
import { RotatingSearchField } from '../molecules/rotating-search-field';
import { closeGlobalSearchEvent, useGlobalSearch } from '../provider';
import { useGlobalRecentSearches } from '../useGlobalRecentSearches';
import { DialogContent, SearchOverlay } from './components';
import { computeStyles } from './compute-styles';
import { useConditionalShellTheme, useViewport, useColorDependentStyles } from './hooks';
import { DIALOG_TRANSITION, SEARCH_ENTITIES } from './styles';
import type { FocusEvent, GlobalSearchMainProps } from './types';

export { TOP_BAR_HEIGHT } from './styles';
export type { StyleConfig } from './types';

export const GlobalSearchMain = ({ children, className }: GlobalSearchMainProps) => {
  const { t } = useTranslation('global-search');

  const isXIconFlex = useMatchMedia({ maxWidth: breakpoints.medium.min + 100 });
  const isXSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const isLarge = useMatchMedia({ minWidth: breakpoints.large.min });

  const { addSearch } = useGlobalRecentSearches();
  const { colorObject } = useShellTheme('colorObject');
  const { isWindows } = shell;

  const [resultsMeasureRef, { height: resultsHeight }] = useMeasure();
  const { hasShellThemeEnabled, heightOffset } = useConditionalShellTheme();

  const {
    searchTerm,
    setSearchTerm,
    isExpandedView,
    setIsExpandedView,
    debouncedSearchTerm,
    setDebouncedSearchTerm,
    searchFor,
    reset,
  } = useGlobalSearch([
    'searchTerm',
    'setSearchTerm',
    'isExpandedView',
    'setIsExpandedView',
    'debouncedSearchTerm',
    'setDebouncedSearchTerm',
    'searchFor',
    'reset',
  ]);

  const { setDebouncedValue } = useSearchHistoryShallowStore('setDebouncedValue');
  const onSearchHistoryChange = useDebouncedFn((value: string) => setDebouncedValue(value), 3000);

  useEffect(() => {
    handleTriggerSearch({
      entities: SEARCH_ENTITIES as unknown as Entity[],
      searchTerm: debouncedSearchTerm,
      uiSource: UiSource.GLOBAL_SEARCH,
    });
  }, [debouncedSearchTerm]);

  const containerRef = useRef<ElementRef<'div'>>(null);
  const [containerMeasureRef, containerBounds] = useMeasure();

  const viewport = useViewport();

  const colorStyles = useColorDependentStyles(colorObject, !!isWindows);

  const styleConfig = useMemo(
    () =>
      computeStyles(
        { isXSmall, isLarge, isWindows: !!isWindows, isExpandedView, hasShellThemeEnabled },
        {
          left: containerBounds.left ?? 0,
          width: containerBounds.width ?? 0,
          height: containerBounds.height ?? 0,
        },
        viewport,
        heightOffset,
        colorStyles
      ),
    [
      isXSmall,
      isLarge,
      isWindows,
      isExpandedView,
      hasShellThemeEnabled,
      containerBounds.left,
      containerBounds.width,
      containerBounds.height,
      viewport,
      heightOffset,
      colorStyles,
    ]
  );

  const onSearchChange = useDebouncedFn((value: string) => setDebouncedSearchTerm(value), 300);

  const searchFieldProps = useControlledField({
    type: 'text',
    value: searchTerm,
    onChange: (value) => {
      setSearchTerm(value);
      onSearchChange(value);
      onSearchHistoryChange(value);
    },
  });

  const { getDialogProps, getTriggerProps, open, close, isOpen } = usePopoverDialog({
    placement: 'bottom',
    middlewareOptions: {
      offset: { mainAxis: styleConfig.dialogOffsetMainAxis },
      size: { padding: 0 },
    },
    onClose: reset,
    transform: false,
  });

  const onSearchSubmit = useCallback(() => {
    if (!debouncedSearchTerm) return;
    setIsExpandedView(true);
  }, [debouncedSearchTerm, setIsExpandedView]);

  useEventListener(
    closeGlobalSearchEvent as any,
    () => {
      reset();
      close();
    },
    true
  );

  const dialogPositionStyle = useMemo(
    () =>
      ({
        '--gs-dialog-width': `${styleConfig.dialogWidth}px`,
        '--gs-dialog-min-width': !isLarge
          ? hasShellThemeEnabled
            ? `min(100% - 40px, ${!isXSmall ? 440 : 520}px)`
            : 'min(100% - 30px, 520px)'
          : '440px',
      } as React.CSSProperties),
    [styleConfig.dialogWidth, isLarge, hasShellThemeEnabled, isXSmall]
  );

  const dialogMotionInitial = useMemo(
    () => ({
      opacity: 0,
      height: 'auto' as const,
      width: styleConfig.dialogWidth,
      top: styleConfig.dialogTop + heightOffset,
      left: styleConfig.dialogLeftPosition,
    }),
    [styleConfig.dialogWidth, styleConfig.dialogTop, styleConfig.dialogLeftPosition, heightOffset]
  );

  const dialogMotionAnimate = useMemo(
    () => ({
      opacity: 1,
      width: styleConfig.dialogAnimatedWidth,
      height: styleConfig.dialogHeight,
      top: styleConfig.dialogAnimatedTop + heightOffset,
      left: styleConfig.dialogLeftPosition,
    }),
    [
      styleConfig.dialogAnimatedWidth,
      styleConfig.dialogHeight,
      styleConfig.dialogAnimatedTop,
      styleConfig.dialogLeftPosition,
      heightOffset,
    ]
  );

  const searchFieldPropsRef = useRef(searchFieldProps);
  searchFieldPropsRef.current = searchFieldProps;

  const onContainerClick = useCallback(() => {
    searchFieldPropsRef.current.onFocus();
    open();
  }, [open]);

  const onTriggerClick = useCallback(() => {
    if (isOpen) {
      close();
    } else {
      searchFieldPropsRef.current.onFocus();
    }
  }, [close, isOpen]);

  const onDialogSearchSubmit = useCallback(() => {
    if (!debouncedSearchTerm) return;
    setIsExpandedView(true);
    addSearch(searchTerm, searchFor);
  }, [debouncedSearchTerm, setIsExpandedView, addSearch, searchTerm, searchFor]);

  const onDialogSearchFocus: FocusEvent = useCallback(
    (e) => {
      searchFieldPropsRef.current.onFocus(e);
      open();
    },
    [open]
  );

  const onDialogSearchBlur: FocusEvent = useCallback((e) => {
    searchFieldPropsRef.current.onBlur(e);
  }, []);

  const onCloseExpandedView = useCallback(() => {
    setIsExpandedView(false);
    close();
  }, [setIsExpandedView, close]);

  const triggerCss = isOpen ? styleConfig.buttonCssOpen : styleConfig.buttonCssClosed;

  const mergedContainerRef = useMemo(() => composeRefs(containerRef, containerMeasureRef), [containerMeasureRef]);

  const rotatingStyleConfig = useMemo(
    () => ({
      ...styleConfig,
      buttonCss: (openState: boolean) => (openState ? styleConfig.buttonCssOpen : styleConfig.buttonCssClosed),
      searchFieldCss: (openState: boolean) =>
        openState ? styleConfig.searchFieldCssOpen : styleConfig.searchFieldCssClosed,
      searchFieldContainerCss: (openState: boolean) =>
        openState ? styleConfig.searchFieldContainerCssOpen : styleConfig.searchFieldContainerCssClosed,
    }),
    [styleConfig]
  );

  return (
    <>
      <div
        ref={mergedContainerRef as React.Ref<HTMLDivElement>}
        className={className}
        css={styleConfig.containerCss}
        onClick={onContainerClick}
      >
        {!isLarge && (
          <Button
            {...getTriggerProps({ onClick: onTriggerClick })}
            variant='secondary'
            trackingId={GlobalSearchTrackingIds.mobileSearchOpen}
            hoverLabel={isOpen ? '' : t('Open Global Search')}
            iconName={hasShellThemeEnabled ? 'search-small' : 'search'}
            size={hasShellThemeEnabled ? 'small' : 'large'}
            className={`global-search__trigger ${isOpen ? 'global-search__trigger--open' : ''}`}
            css={[
              triggerCss,
              hasShellThemeEnabled &&
                css`
                  span,
                  svg {
                    min-width: 20px;
                    min-height: 20px;
                  }
                `,
            ]}
          />
        )}

        {isLarge && (
          <RotatingSearchField
            searchFieldProps={searchFieldProps}
            styleConfig={rotatingStyleConfig}
            isOpen={isOpen}
            dataTrackingid={GlobalSearchTrackingIds.searchField}
            onSearchSubmit={onSearchSubmit}
          />
        )}

        <PopoverDialog
          initial={dialogMotionInitial}
          animate={dialogMotionAnimate}
          exit={undefined}
          transition={DIALOG_TRANSITION}
          {...getDialogProps()}
          css={styleConfig.dialogCss}
          style={dialogPositionStyle}
        >
          <DialogContent
            searchFieldProps={searchFieldProps}
            styleConfig={styleConfig}
            isExpandedView={isExpandedView}
            isXIconFlex={isXIconFlex}
            isXSmall={isXSmall}
            debouncedSearchTerm={debouncedSearchTerm}
            resultsMeasureRef={resultsMeasureRef}
            resultsHeight={resultsHeight}
            onDialogSearchSubmit={onDialogSearchSubmit}
            onDialogSearchFocus={onDialogSearchFocus}
            onDialogSearchBlur={onDialogSearchBlur}
            onCloseExpandedView={onCloseExpandedView}
          >
            {children}
          </DialogContent>
        </PopoverDialog>
      </div>

      {isOpen && <SearchOverlay />}
    </>
  );
};
